import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { formattedError } from '@helpers/functions';
import { AlertService } from '@shared/alert/alert.service';
import { DadosColaborador, DataColab } from 'src/app/models';
import { environment } from 'src/environments/environment';

import { ColaboradorService } from '@modules/colaboradores/services/colaborador.service';
import { DadosLogin } from '../interfaces/dadoslogin.interface';
import { AuthCheckService } from './auth-check.service';
import { PermissionService } from './permission.service';
import { PortalService } from './portal.service';

@Injectable({
    providedIn: 'root',
})
export class DadosLoginService {
    private url = environment.apiUrl;
    private readonly reload$ = new BehaviorSubject<void>(undefined);
    readonly dadosLogin$: Observable<DadosLogin>;

    current_user: DadosColaborador | undefined;
    dados_login: DadosLogin | undefined;

    constructor(
        private alertService: AlertService,
        private authCheckService: AuthCheckService,
        private colaboradorService: ColaboradorService,
        private http: HttpClient,
        private permissionService: PermissionService,
        private portalService: PortalService,
        private toastrService: ToastrService
    ) {
        this.dadosLogin$ = this.reload$.pipe(
            switchMap(() => {
                if (!this.authCheckService.isLogged) {
                    return of(null as DadosLogin);
                }
                return this.http.get<DadosLogin>(
                    `${this.url}colaboradores/dadoslogin/teste/`
                );
            }),
            tap(dados => {
                if (dados) {
                    this.dados_login = dados;
                    this.portalService.setPortais(dados.portais);
                    this.permissionService.setUserPermissions(dados.permissoes);
                    if (dados.dados?.id_colaborador) {
                        this.colaboradorService
                            .loadColaboradorLogado(dados.dados.id_colaborador)
                            .subscribe();
                    }
                } else {
                    this.portalService.setPortais([]);
                    this.permissionService.clearPermissions();
                    this.colaboradorService.clearColaboradorLogado();
                }
            }),
            catchError(err => {
                this.toastrService.error('Erro na autenticação');
                this.alertService.error(formattedError(err), true);
                this.clearAllData();
                return EMPTY;
            }),
            shareReplay(1)
        );
        this.dadosLogin$.subscribe();
    }

    public reload(): void {
        this.reload$.next(undefined);
    }

    public clearAllData(): void {
        this.dados_login = undefined;
        this.current_user = undefined;
        this.permissionService.clearPermissions();
        this.portalService.setPortais([]);
        this.colaboradorService.clearColaboradorLogado();
    }

    public getCurrentColaborador() {
        return this.dados_login
            ? of(this.dados_login.dados)
            : this.http
                  .get<DataColab>(`${this.url}colaboradoresdadosbasicos/`)
                  .pipe(map(res => res[0]));
    }

    public getContadores() {
        return this.dados_login?.contadores;
    }
}
